import { supabase } from './supabaseClient';

const BUCKET_NAME = 'album-art';

export const sanitizeFilename = (filename: string): string => {
  const ext = filename.substring(filename.lastIndexOf('.'));
  const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));

  return nameWithoutExt
    .toLowerCase()
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u')
    .replace(/[ñ]/g, 'n')
    .replace(/[ç]/g, 'c')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') + ext.toLowerCase();
};

export const getAlbumArtUrl = (filename: string, bustCache = false): string => {
  if (!filename || filename === '/placeholder-civicbeat.svg') {
    return '/placeholder-civicbeat.svg';
  }

  const { data } = supabase.storage
    .from(BUCKET_NAME)
    .getPublicUrl(filename);

  // Add cache-busting parameter when needed
  if (bustCache) {
    const url = new URL(data.publicUrl);
    url.searchParams.set('t', Date.now().toString());
    return url.toString();
  }

  return data.publicUrl;
};

export const uploadAlbumArt = async (
  file: File,
  customFilename?: string
): Promise<{ url: string; path: string } | { error: string }> => {
  try {
    const filename = customFilename || sanitizeFilename(file.name);

    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(filename, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (error) {
      return { error: error.message };
    }

    return {
      path: data.path,
      url: getAlbumArtUrl(data.path, true),
    };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Upload failed' };
  }
};

export const deleteAlbumArt = async (filename: string): Promise<{ error?: string }> => {
  try {
    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([filename]);

    if (error) {
      return { error: error.message };
    }

    return {};
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Delete failed' };
  }
};

export const listAlbumArt = async (): Promise<{ files: string[]; error?: string }> => {
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .list();

    if (error) {
      return { files: [], error: error.message };
    }

    return {
      files: data.map(file => file.name),
    };
  } catch (err) {
    return {
      files: [],
      error: err instanceof Error ? err.message : 'List failed',
    };
  }
};
